// Capacitor modules imported dynamically inside async functions to avoid TDZ
import { fmtErr } from "@/lib/utils";

// Lazy Capacitor helpers to avoid TDZ
async function getShare() {
  const { Share } = await import("@capacitor/share");
  return Share;
}
async function getFs() {
  const mod = await import("@capacitor/filesystem");
  return { Filesystem: mod.Filesystem, Directory: mod.Directory };
}

// storyCard imported dynamically inside async functions to avoid TDZ in Capacitor WebView
const storyCardModule = () => import("@/lib/storyCard");

export type ShareMode = "experience-card" | "story-set" | "photo-card" | "photo-cards" | "card-only" | "selected-cards";
export type ShareTarget = "instagram-story" | "whatsapp" | "snapchat" | "tiktok" | "messages" | "system";
export type ShareTone = "fire" | "mid" | "vibe" | "honest" | "elite" | "cozy" | "lowkey";

export const SHARE_TONES: { id: ShareTone; label: string; caption: (place: string) => string }[] = [
  { id: "fire", label: "🔥 fire", caption: (p) => `${p} absolutely ate` },
  { id: "vibe", label: "✨ vibes", caption: (p) => `vibes at ${p} were unreal` },
  { id: "honest", label: "💯 honest", caption: (p) => `${p} was solid. not life-changing but solid` },
  { id: "mid", label: "😐 mid", caption: (p) => `${p} was mid ngl` },
  { id: "elite", label: "👑 elite", caption: (p) => `${p} if you know you know` },
  { id: "cozy", label: "🫶 cozy", caption: (p) => `little dinner moment at ${p}` },
  { id: "lowkey", label: "😏 lowkey", caption: (p) => `lowkey ${p} kinda slaps` },
];

export function getToneCoverCopy(tone: ShareTone, restaurantName: string): string {
  switch (tone) {
    case "fire": return `${restaurantName} absolutely ate`;
    case "vibe": return `vibes at ${restaurantName} were unreal`;
    case "honest": return `${restaurantName} was solid. not life-changing but solid`;
    case "mid": return `${restaurantName} was mid ngl`;
    case "elite": return `${restaurantName} if you know you know`;
    case "cozy": return `little dinner moment at ${restaurantName}`;
    case "lowkey": return `lowkey ${restaurantName} kinda slaps`;
    default: return `${restaurantName} absolutely ate`;
  }
}

export interface ShareableDish {
  name: string;
  imageUrl?: string;
  price?: string;
  description?: string;
}

export interface ShareContext {
  restaurantName: string;
  items: ShareableDish[];
  date?: string;
  shareMode: ShareMode;
  tone?: ShareTone;
  groupPhotoUrl?: string | null;
  locationLabel?: string;
  dishName?: string;
  rating?: "loved" | "not-for-me";
  /**
   * Optional map of already-decoded <img> elements keyed by src URL, for
   * the v2 renderers to reuse instead of re-decoding ~80–120KB data: URLs.
   */
  preloaded?: Map<string, HTMLImageElement> | Record<string, HTMLImageElement>;
}

export interface ShareTargetDefinition {
  id: ShareTarget;
  label: string;
  shortLabel: string;
  description: string;
  usesCaption: boolean;
  preferredMode?: ShareMode;
}

export interface PreparedSharePayload {
  target: ShareTarget;
  title: string;
  text: string;
  files?: File[];
  fileNameBase: string;
}

const APP_LINK = "https://yumz.social";

export const SHARE_TARGETS: ShareTargetDefinition[] = [
  {
    id: "instagram-story",
    label: "Instagram Story",
    shortLabel: "Instagram",
    description: "Story-sized image, ready for IG story flow",
    usesCaption: false,
    preferredMode: "photo-cards",
  },
  {
    id: "snapchat",
    label: "Snapchat",
    shortLabel: "Snapchat",
    description: "Share via Snapchat Creative Kit",
    usesCaption: false,
    preferredMode: "photo-cards",
  },
  {
    id: "tiktok",
    label: "TikTok",
    shortLabel: "TikTok",
    description: "Share via TikTok share sheet",
    usesCaption: false,
    preferredMode: "photo-cards",
  },
  {
    id: "whatsapp",
    label: "WhatsApp",
    shortLabel: "WhatsApp",
    description: "Caption-first share with image when available",
    usesCaption: true,
    preferredMode: "card-only",
  },
  {
    id: "messages",
    label: "Messages",
    shortLabel: "Messages",
    description: "Clean card plus share text for iMessage/SMS",
    usesCaption: true,
    preferredMode: "card-only",
  },
  {
    id: "system",
    label: "More",
    shortLabel: "More",
    description: "Open the native share sheet",
    usesCaption: true,
  },
];

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "yumz";
}

function buildMenuCaption(context: ShareContext): string {
  const dishNames = context.items.slice(0, 4).map((item) => item.name);
  const intro = `yumzing at ${context.restaurantName}`;
  const dishes = dishNames.length ? `\n${dishNames.map((name) => `• ${name}`).join("\n")}` : "";
  const location = context.locationLabel ? `\n📍 ${context.locationLabel}` : "";
  return `${intro}${location}${dishes}\n\nget yumz: ${APP_LINK}`;
}

function buildDishCaption(context: ShareContext): string {
  const primaryDish = context.dishName || context.items[0]?.name || "this dish";
  const location = context.locationLabel ? ` · ${context.locationLabel}` : "";
  return `${primaryDish} at ${context.restaurantName}${location}\n\nget yumz: ${APP_LINK}`;
}

function fileFromBlob(blob: Blob, name: string): File {
  return new File([blob], name, { type: blob.type || "image/png" });
}

export async function prepareSharePayload(
  target: ShareTarget,
  context: ShareContext,
): Promise<PreparedSharePayload> {
  const fileNameBase = slugify(`${context.restaurantName}-${context.items[0]?.name || "menu"}`);
  const title = context.dishName
    ? `${context.dishName} at ${context.restaurantName}`
    : `yumzing at ${context.restaurantName}`;

  const text = context.dishName ? buildDishCaption(context) : buildMenuCaption(context);

  // ─── v2 default routing (Wave C) ─────────────────────────────────────
  // v2 is ON by default. Set localStorage.yumzCardsV2 = "0" to disable for
  // emergency rollback. For dish-centric share modes (card-only or
  // explicit dishName with a dish image), route to renderVerdict (food
  // poster layout). Other modes (story-set, experience-card, photo-cards)
  // still use the v1 generators until v2 has a multi-card path.
  const v2Enabled =
    typeof window === "undefined" ||
    (() => {
      try {
        return window.localStorage?.getItem("yumzCardsV2") !== "0";
      } catch {
        return true;
      }
    })();
  if (v2Enabled) {
    try {
      const firstDish = context.items[0];
      if (firstDish?.imageUrl && (context.dishName || context.shareMode === "card-only")) {
        const { renderVerdict } = await import("@/lib/shareCardVerdict");
        const { mapToneToStatus } = await import("@/lib/shareStatus");
        const blob = await renderVerdict({
          restaurantName: context.restaurantName,
          dishName: context.dishName || firstDish.name,
          dishImageUrl: firstDish.imageUrl,
          status: mapToneToStatus(context.tone),
          locationLabel: context.locationLabel,
          preloaded: context.preloaded,
        });
        return {
          target,
          title,
          text,
          files: [fileFromBlob(blob, `${fileNameBase}-verdict.jpg`)],
          fileNameBase,
        };
      }
    } catch (e) {
      console.error("[v2 prepareSharePayload] failed, falling back to v1", fmtErr(e));
      // intentional fall-through to v1 below
    }
  }

  // ── New share modes ──────────────────────────────────────────────────────────

  if (context.shareMode === "experience-card") {
    const blob = await (await storyCardModule()).generateExperienceCard({
      restaurantName: context.restaurantName,
      locationLabel: context.locationLabel,
      items: context.items.slice(0, 3),
      tone: context.tone,
    });
    return {
      target,
      title,
      text,
      files: [fileFromBlob(blob, `${fileNameBase}-experience.jpg`)],
      fileNameBase,
    };
  }

  if (context.shareMode === "story-set") {
    const blobs = await (await storyCardModule()).generateStorySet({
      restaurantName: context.restaurantName,
      locationLabel: context.locationLabel,
      items: context.items,
      tone: context.tone,
    });
    return {
      target,
      title,
      text,
      files: blobs.map((blob, i) =>
        fileFromBlob(blob, `${fileNameBase}-story-${i + 1}.jpg`),
      ),
      fileNameBase,
    };
  }

  if (context.shareMode === "photo-card") {
    const blob = await (await storyCardModule()).generateStoryCard({
      photoUrl: context.groupPhotoUrl || context.items[0]?.imageUrl || "",
      dishName: context.items.map((item) => item.name).join(", "),
      restaurantName: context.restaurantName,
      selectedDishes: context.items.map((item) => ({ name: item.name, imageUrl: item.imageUrl })),
      groupPhotoUrl: context.groupPhotoUrl || undefined,
      tone: context.tone,
    });
    return {
      target,
      title,
      text,
      files: [fileFromBlob(blob, `${fileNameBase}-photo.jpg`)],
      fileNameBase,
    };
  }

  // ── Legacy modes ──────────────────────────────────────────────────────────────

  if (target === "instagram-story") {
    const blob = await (await storyCardModule()).generateStoryCard({
      photoUrl: context.groupPhotoUrl || context.items[0]?.imageUrl || "",
      dishName: context.dishName || context.items.map((item) => item.name).join(", "),
      restaurantName: context.restaurantName,
      rating: context.rating,
      selectedDishes: context.dishName
        ? undefined
        : context.items.map((item) => ({ name: item.name, imageUrl: item.imageUrl })),
      groupPhotoUrl: context.groupPhotoUrl || undefined,
    });

    return {
      target,
      title,
      text,
      files: [fileFromBlob(blob, `${fileNameBase}-story.jpg`)],
      fileNameBase,
    };
  }

  if (context.shareMode === "selected-cards" && !context.dishName) {
    const blobs: Blob[] = [];
    for (const item of context.items) {
      blobs.push(
        await (await storyCardModule()).generateDishCard({
          dishName: item.name,
          restaurantName: context.restaurantName,
          price: item.price,
          description: item.description,
          imageUrl: item.imageUrl,
        }),
      );
    }

    return {
      target,
      title,
      text,
      files: blobs.map((blob, index) =>
        fileFromBlob(blob, `${fileNameBase}-${slugify(context.items[index]?.name || `dish-${index + 1}`)}.jpg`),
      ),
      fileNameBase,
    };
  }

  if (context.shareMode === "photo-cards" && !context.dishName) {
    const blob = await (await storyCardModule()).generateStoryCard({
      photoUrl: context.groupPhotoUrl || context.items[0]?.imageUrl || "",
      dishName: context.items.map((item) => item.name).join(", "),
      restaurantName: context.restaurantName,
      selectedDishes: context.items.map((item) => ({ name: item.name, imageUrl: item.imageUrl })),
      groupPhotoUrl: context.groupPhotoUrl || undefined,
    });

    return {
      target,
      title,
      text,
      files: [fileFromBlob(blob, `${fileNameBase}-story.jpg`)],
      fileNameBase,
    };
  }

  const firstDish = context.items[0];
  const blob = await (await storyCardModule()).generateDishCard({
    dishName: context.dishName || firstDish?.name || "Dish",
    restaurantName: context.restaurantName,
    price: firstDish?.price,
    description: firstDish?.description,
    imageUrl: firstDish?.imageUrl,
  });

  return {
    target,
    title,
    text,
    files: [fileFromBlob(blob, `${fileNameBase}-card.jpg`)],
    fileNameBase,
  };
}

function withTimeout<T>(promise: Promise<T>, ms: number, label = "operation"): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)),
  ]);
}

export async function sharePreparedPayload(payload: PreparedSharePayload): Promise<boolean> {
  // ── Always use Capacitor Share on native (most reliable on iOS) ────────────
  if (((await import("@capacitor/core")).Capacitor.isNativePlatform()) && payload.files?.length) {
    try {
      const file = payload.files[0];
      const arrayBuffer = await file.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          "",
        ),
      );
      const fileName = `yumz-share-${Date.now()}.jpg`;
      const { Filesystem, Directory } = await getFs();
      const Share = await getShare();
      const result = await withTimeout(
        Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Cache,
          recursive: true,
        }),
        10000,
        "file write",
      );
      await withTimeout(
        Share.share({
          title: payload.title,
          text: payload.text,
          url: result.uri,
          dialogTitle: "Share your yumz",
        }),
        20000,
        "native share",
      );
      return true;
    } catch (error: unknown) {
      if ((error as Error)?.name === "AbortError") return false;
      console.warn("Native share failed, falling through:", (error as Error)?.message);
      // Fall through to web share
    }
  }

  // ── 2. Web navigator.share (works on mobile web / PWA) ──────────────────────
  try {
    if (navigator.share) {
      const shareData: ShareData = {
        title: payload.title,
        text: payload.target === "instagram-story" ? undefined : payload.text,
      };

      if (payload.files?.length && navigator.canShare?.({ files: payload.files })) {
        shareData.files = payload.files;
      }

      await navigator.share(shareData);
      return true;
    }
  } catch (error: unknown) {
    if ((error as Error)?.name === "AbortError") {
      return false;
    }
  }

  // ── 3. Fallback: download files directly ─────────────────────────────────────
  if (payload.files?.length) {
    for (const file of payload.files) {
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    }
    return true;
  }

  // ── 4. Last resort: copy text to clipboard ────────────────────────────────────
  await navigator.clipboard.writeText(payload.text);
  return true;
}

// ── iOS Custom URL Scheme helpers ─────────────────────────────────────────────

// Wave N — native Instagram Stories bridge (see AppDelegate.swift
// InstagramSharePlugin). Registered lazily; on web / non-iOS the proxy's
// methods simply reject and we fall back to the system share sheet.
interface InstagramSharePlugin {
  isAvailable(): Promise<{ available: boolean }>;
  shareToStories(opts: { imageBase64: string }): Promise<{ opened: boolean }>;
}
let _igPlugin: InstagramSharePlugin | null = null;
async function getInstagramPlugin(): Promise<InstagramSharePlugin> {
  if (!_igPlugin) {
    const { registerPlugin } = await import("@capacitor/core");
    _igPlugin = registerPlugin<InstagramSharePlugin>("InstagramShare");
  }
  return _igPlugin;
}

async function fileToBase64(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  return btoa(new Uint8Array(arrayBuffer).reduce((d, b) => d + String.fromCharCode(b), ""));
}

/**
 * Open Instagram Stories with the card pre-loaded as the background image,
 * via the native instagram-stories:// flow (UIPasteboard sticker keys). On iOS
 * with IG installed this drops the user straight into the Stories composer.
 * Falls back to the system share sheet on web, when IG isn't installed, or on
 * any native error — so it never regresses the existing behaviour.
 */
export async function openInstagramStories(storyFile: File): Promise<boolean> {
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (Capacitor.isNativePlatform()) {
      try {
        const ig = await getInstagramPlugin();
        const { available } = await ig.isAvailable();
        if (available) {
          const imageBase64 = await fileToBase64(storyFile);
          const { opened } = await ig.shareToStories({ imageBase64 });
          if (opened) return true;
        }
        // not installed / didn't open → fall through to the share sheet
      } catch {
        // plugin missing or threw → fall through to the share sheet
      }
    }
  } catch { /* ignore — fall through */ }

  // Fallback: system share sheet (previous behaviour, most reliable on iOS).
  try {
    const base64 = await fileToBase64(storyFile);
    const fileName = `yumz-story-${Date.now()}.jpg`;
    const { Filesystem, Directory } = await getFs();
    const Share = await getShare();
    const written = await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Cache });
    await Share.share({ title: "Share to Instagram", url: written.uri, dialogTitle: "Share to Instagram" });
    return true;
  } catch { return false; }
}

/**
 * Attempt to send an image via WhatsApp.
 * Uses navigator.share() with the File object for proper image sharing,
 * since the whatsapp:// URL scheme only supports text, not images.
 */
export async function openWhatsApp(attachmentFile: File): Promise<boolean> {
  try {
    // Best approach: use navigator.share() with the file — WhatsApp appears in share sheet
    if (navigator.share && navigator.canShare?.({ files: [attachmentFile] })) {
      const text = `this dish kinda ate. get yumz: ${APP_LINK}`;
      await navigator.share({
        text,
        files: [attachmentFile],
      });
      return true;
    }

    // Native fallback: write file and use Capacitor Share
    if (((await import("@capacitor/core")).Capacitor.isNativePlatform()) && attachmentFile) {
      try {
        const arrayBuffer = await attachmentFile.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(arrayBuffer).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            "",
          ),
        );
        const fileName = `yumz-wa-${Date.now()}.png`;
        const { Filesystem, Directory } = await getFs();
        const Share = await getShare();
        const result = await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Cache,
          recursive: true,
        });
        await Share.share({
          title: "Share via WhatsApp",
          text: "check out this dish I found on yumz! 🔥",
          url: result.uri,
          dialogTitle: "Share",
        });
        return true;
      } catch {
        // Fall through to plain text
      }
    }
    // Last resort: open WhatsApp with a pre-filled text message
    const text = `this dish kinda ate. get yumz: ${APP_LINK}`;
    const waUrl = `whatsapp://send?text=${encodeURIComponent(text)}`;
    const popup = window.open(waUrl, "_blank");
    try { if (popup) popup.close(); } catch {}
    return true;
  } catch {
    return false;
  }
}

/**
 * Attempt to share via Snapchat using Creative Kit URL scheme.
 * Falls back to system share if Snapchat isn't installed.
 */
export async function openSnapchat(file: File): Promise<boolean> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const base64 = btoa(new Uint8Array(arrayBuffer).reduce((d, b) => d + String.fromCharCode(b), ""));
    const fileName = `yumz-snap-${Date.now()}.jpg`;
    const { Filesystem, Directory } = await getFs();
    const Share = await getShare();
    const written = await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Cache });
    await Share.share({ title: "Share to Snapchat", url: written.uri, dialogTitle: "Share to Snapchat" });
    return true;
  } catch { return false; }
}

/**
 * Attempt to share via TikTok.
 * TikTok doesn't have a reliable deep link for sharing media from third-party apps.
 * Uses system share with TikTok as a preferred activity.
 */
export async function openTikTok(file: File): Promise<boolean> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const base64 = btoa(new Uint8Array(arrayBuffer).reduce((d, b) => d + String.fromCharCode(b), ""));
    const fileName = `yumz-tiktok-${Date.now()}.jpg`;
    const { Filesystem, Directory } = await getFs();
    const Share = await getShare();
    const written = await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Cache });
    await Share.share({ title: "Share to TikTok", url: written.uri, dialogTitle: "Share to TikTok" });
    return true;
  } catch { return false; }
}

/**
 * Open the iOS SMS composer via the sms: URL scheme.
 */
export function openMessages(text: string): boolean {
  try {
    // Strip emoji that can cause encoding issues in sms: URLs
    const cleanText = encodeURIComponent(text);
    window.location.href = `sms:?body=${cleanText}`;
    return true;
  } catch {
    return false;
  }
}

/**
 * Save a generated image blob to the device photo library.
 * - On iOS/Android native: uses @capacitor/filesystem + Photos plugin
 *   (requires @capacitor/camera — already installed — for saveToGallery).
 *   The current codebase uses Camera plugin; we implement a JS-side save
 *   using a download link as the most universally working approach.
 * - On web: creates a temporary <a> download link.
 */
export async function saveImageToPhotoLibrary(file: File): Promise<boolean> {
  if (((await import("@capacitor/core")).Capacitor.isNativePlatform())) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          "",
        ),
      );
      const fileName = `yumz-${Date.now()}.png`;
      const { Filesystem, Directory } = await getFs();
      const result = await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Cache,
        recursive: true,
      });
      // Use the URI returned by writeFile — not a manually constructed path
      await (await getShare()).share({
        title: "Save to Photos",
        url: result.uri,
        dialogTitle: "Save to Photos",
      });
      return true;
    } catch (err) {
      console.warn("saveImageToPhotoLibrary native failed:", (err as Error)?.message);
      // Fall through to download link
    }
  }

  // Web / fallback: trigger download
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  a.click();
  URL.revokeObjectURL(url);
  return true;
}
